import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import {
  apiClient,
  TenantDetailsLoaderResult,
  TenantDetailsLeaseLoaderResult,
} from "~/services/apiClient";
import { FormattedServiceRequestData, LoaderData } from "~/types";
import {
  resolveActorUserName,
  getFilteredRequestList,
  getAuthWrapper,
  INTENTS,
  loadCookiesData,
  ROUTES,
  getReadyAuthUser,
} from "~/utils";
import * as APITypes from "~/API";
import type { TenantDetailsGenerateShadowInviteResult } from "~/types/tenantDetailsAction";
import { message } from "antd";
import i18n from "~/i18n";

export const TenantDetailsPageLoader = async ({
  request,
  params,
}: LoaderFunctionArgs): Promise<TenantDetailsPageLoaderType | Response> => {
  const auth = getAuthWrapper();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;

  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const userName = await resolveActorUserName(user, authAccessInfo);
  const tenantID = params.tenantID || "";

  const [result, leaseResult] = await Promise.all([
    apiClient.loader<TenantDetailsLoaderResult>("tenant-details", {
      id: tenantID,
      ownerID,
      ownerType,
      userID: user.uid,
    }),
    apiClient.loader<TenantDetailsLeaseLoaderResult>("tenant-details-lease", {
      id: tenantID,
    }),
  ]);

  if (!result.success || !result.data) {
    console.error("Failed to load tenant details:", result.error);
    return redirect(ROUTES.tenantTable);
  }

  const data = result.data;
  const formattedLeaseList =
    leaseResult.success && leaseResult.data
      ? leaseResult.data.formattedLeaseList
      : [];
  const formattedServiceRequestList = getFilteredRequestList({
    filters: {},
    requestList:
      data.formattedServiceRequestList as FormattedServiceRequestData[],
    isAdminOrSuperAdmin,
  });

  return {
    tenantDetails: {
      success: true,
      data: data.tenantDetails,
    } as LoaderData<APITypes.Tenant>,
    formattedServiceRequestList,
    tenantList: data.tenantList,
    ownerID: user?.uid || "",
    ownerType: APITypes.OwnerType.User,
    userName,
    canCreateRequest:
      isAdminOrSuperAdmin || data.permissions.canCreateServiceRequest,
    formattedLeaseList,
    stays: data.stays ?? [],
  };
};

export type TenantDetailsPageLoaderType = {
  tenantDetails: LoaderData<APITypes.Tenant>;
  formattedServiceRequestList: FormattedServiceRequestData[];
  tenantList: APITypes.Tenant[];
  ownerID: string;
  ownerType: APITypes.OwnerType;
  userName: string;
  canCreateRequest: boolean;
  formattedLeaseList: TenantDetailsLeaseLoaderResult["formattedLeaseList"];
  stays: APITypes.Stay[];
};

export type { TenantDetailsGenerateShadowInviteResult };

export const TenantDetailsPageAction = async ({
  request,
  params,
}: ActionFunctionArgs) => {
  const auth = getAuthWrapper();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");

  const tenantID = params.tenantID || "";
  const formData = await request.formData();
  const intent = formData.get("intent");

  switch (intent) {
    case INTENTS._delete_tenant: {
      const bodyStr = formData.get("body") as string;
      const payload = (bodyStr ? JSON.parse(bodyStr) : {}) as Record<
        string,
        unknown
      >;
      const tenantType = payload.tenantType as APITypes.TenantType | undefined;

      if (tenantType === APITypes.TenantType.AuthenticatedTenant) {
        message.error(i18n.t("tenant_deletion_error_authenticated"));
        return { error: "tenant_deletion_error_authenticated" };
      }

      const { tenantType: _tenantType, ...deletePayload } = payload;
      const result = await apiClient.sendIntent<{ deleted: boolean; id: string }>(
        INTENTS._delete_tenant,
        { id: tenantID, tenantID, ...deletePayload }
      );
      if (!result.success) {
        const errorKey = result.error || "tenant_deletion_failed";
        message.error(
          i18n.t(
            errorKey as
              | "tenant_deletion_error_authenticated"
              | "tenant_deletion_error_active_leases"
              | "tenant_deletion_error_running_requests"
              | "tenant_deletion_failed"
          )
        );
        return { error: errorKey };
      }
      message.success(i18n.t("tenant_deletion_success"));
      return redirect(ROUTES.tenantTable);
    }
    case INTENTS._generate_shadow_invite: {
      const bodyStr = formData.get("body") as string;
      const payload = (bodyStr ? JSON.parse(bodyStr) : {}) as {
        tenantID?: string;
        leaseID?: string;
        stayID?: string;
        tenantType?: APITypes.TenantType;
      };

      if (payload.tenantID !== tenantID) {
        return {
          ok: false as const,
          error: "Shadow_invite_generate_failed",
        };
      }

      if (payload.tenantType === APITypes.TenantType.AuthenticatedTenant) {
        return {
          ok: false as const,
          error: "shadow_invite_error_not_shadow_tenant",
        };
      }

      if (!payload.leaseID && !payload.stayID) {
        return {
          ok: false as const,
          error: "Shadow_invite_select_context",
        };
      }

      const result = await apiClient.sendIntent<{
        shadowTenantId: string;
        leaseId?: string;
        stayId?: string;
      }>(INTENTS._generate_shadow_invite, {
        tenantID,
        ...(payload.leaseID ? { leaseID: payload.leaseID } : {}),
        ...(payload.stayID ? { stayID: payload.stayID } : {}),
      });

      if (
        !result.success ||
        !result.data?.shadowTenantId ||
        (!result.data.leaseId && !result.data.stayId)
      ) {
        const err = result.error || "Shadow_invite_generate_failed";
        return { ok: false as const, error: err };
      }

      return {
        ok: true as const,
        shadowTenantId: result.data.shadowTenantId,
        ...(result.data.leaseId ? { leaseId: result.data.leaseId } : {}),
        ...(result.data.stayId ? { stayId: result.data.stayId } : {}),
      };
    }
  }
  return {};
};
